// NotificationsSection.tsx — Settings → Notifications.
//
// Two switches per category: In app (is a notification row created and listed)
// and Push (does it reach the phone). They are not fully independent server-side
// — push is generated FROM the in-app row — but the user-visible contract is
// simple: off means you do not get it through that channel.
//
// Every switch here is enforced in backend/apps/social/notifications.py:emit and
// the notifications list endpoint (see test_notification_prefs.py): no
// decorative toggles.
import { DiscussionSettings } from '@/features/discussions/DiscussionSettings';
import { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { Panel, Switch, Avatar, useToast } from '@/ui';
import { Icon } from '@/icons';
import { useSession } from '@/hooks';
import { followsService } from '@/core/services/followsService';
import { NOTIFICATION_CATEGORIES } from '@/core/models/account';
import type {
  FollowedCreator,
  NotificationCategory,
  NotificationPrefs,
} from '@/core/models/account';

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const Card = styled(Panel)`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const CardTitle = styled.h3`
  margin: 0 0 8px;
  font-family: ${({ theme }) => theme.font.display};
  font-size: ${({ theme }) => theme.fontSize.md};
  color: ${({ theme }) => theme.color.textPrimary};
`;

/** Column headers sit above the switch columns so each one is labelled once. */
const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid ${({ theme }) => theme.color.glassBorder};
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 0;
  border-bottom: 1px solid ${({ theme }) => theme.color.glassBorder};

  &:last-of-type {
    border-bottom: none;
  }
`;

const RowText = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const RowLabel = styled.span`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textPrimary};
`;

const RowHint = styled.span`
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
  max-width: 52ch;
`;

/** Fixed-width so the two switch columns line up down the card. */
const Cell = styled.div`
  width: 56px;
  flex: none;
  display: flex;
  justify-content: center;
`;

const ColumnLabel = styled.span`
  width: 56px;
  flex: none;
  text-align: center;
  font-size: ${({ theme }) => theme.fontSize.xs};
  font-weight: 600;
  color: ${({ theme }) => theme.color.textMuted};
`;

const LockedNote = styled.span`
  width: 112px;
  flex: none;
  text-align: center;
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
`;

const Disclosure = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 12px 4px;
  background: none;
  border: none;
  cursor: pointer;
  text-align: left;
  color: ${({ theme }) => theme.color.textPrimary};
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.focusRing};
    outline-offset: 2px;
    border-radius: ${({ theme }) => theme.radius.sm};
  }
`;

const Chevron = styled.span<{ $open: boolean }>`
  display: inline-flex;
  color: ${({ theme }) => theme.color.textMuted};
  transform: rotate(${({ $open }) => ($open ? 180 : 0)}deg);
  transition: transform 0.15s ease;
`;

const CreatorRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 4px 8px 26px;
`;

const CreatorName = styled.span`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textPrimary};
`;

const CreatorHandle = styled.span`
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
`;

const Muted = styled.p`
  margin: 0;
  padding: 8px 4px 8px 26px;
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
`;

const CATEGORY_META: Record<NotificationCategory, { label: string; hint: string }> = {
  posts: {
    label: 'New posts from people you follow',
    hint: 'Tells you when someone you follow publishes. Choose individual creators below.',
  },
  dms: {
    label: 'Direct messages',
    hint: 'A new message in a conversation you are part of.',
  },
  comments: {
    label: 'Comments and replies',
    hint: 'Replies to your posts, and replies to your comments.',
  },
  likes: { label: 'Likes', hint: 'Someone liked your post.' },
  cherishes: { label: 'Cherishes', hint: 'Someone cherished your post.' },
  spreads: { label: 'Spreads', hint: 'Someone spread your post to their followers.' },
  mentions: {
    label: 'Mentions',
    hint: 'Someone @-mentioned you. Who may mention you at all is set in Privacy & Security.',
  },
  follows: { label: 'New followers', hint: 'Someone started following you.' },
  quotes: { label: 'Quotes', hint: 'Someone quoted your post with their own take.' },
};

const DEFAULT_CHANNELS = { inApp: true, push: true };

export function NotificationsSection() {
  const { currentUser, updateProfile } = useSession();
  const toast = useToast();

  const [creators, setCreators] = useState<FollowedCreator[] | null>(null);
  const [creatorsOpen, setCreatorsOpen] = useState(false);
  const [creatorsError, setCreatorsError] = useState(false);

  // Load the followed list once the disclosure is first opened — it is a second
  // request and most visitors to this screen never expand it.
  useEffect(() => {
    if (!creatorsOpen || creators !== null) return;
    let cancelled = false;
    followsService
      .followedCreators()
      .then((rows) => {
        if (!cancelled) setCreators(rows);
      })
      .catch(() => {
        if (!cancelled) setCreatorsError(true);
      });
    return () => {
      cancelled = true;
    };
  }, [creatorsOpen, creators]);

  const prefs = currentUser?.notificationPrefs as NotificationPrefs | undefined;

  const channelsFor = useCallback(
    (category: NotificationCategory) => prefs?.[category] ?? DEFAULT_CHANNELS,
    [prefs],
  );

  async function setChannel(
    category: NotificationCategory,
    channel: 'inApp' | 'push',
    next: boolean,
  ) {
    await updateProfile({ notificationPrefs: { [category]: { [channel]: next } } });
    toast(
      next
        ? `${CATEGORY_META[category].label}: ${channel === 'push' ? 'push' : 'in-app'} on`
        : `${CATEGORY_META[category].label}: ${channel === 'push' ? 'push' : 'in-app'} off`,
    );
  }

  async function setCreator(handle: string, next: boolean) {
    // Optimistic: the row flips immediately, and reverts if the write fails.
    setCreators((rows) =>
      rows ? rows.map((c) => (c.handle === handle ? { ...c, notifyPosts: next } : c)) : rows,
    );
    try {
      await followsService.setCreatorNotify(handle, next);
    } catch {
      setCreators((rows) =>
        rows ? rows.map((c) => (c.handle === handle ? { ...c, notifyPosts: !next } : c)) : rows,
      );
      toast('Could not update that creator. Please try again');
    }
  }

  if (!currentUser) return null;

  const postsOff = !channelsFor('posts').inApp && !channelsFor('posts').push;

  return (
    <Stack>
      <DiscussionSettings notifications />
      <Card>
        <CardTitle>What you get notified about</CardTitle>
        <HeaderRow>
          <RowText>
            <RowHint>
              In app shows it in your Notifications list. Push sends it to the TruXSocial app on
              your phone.
            </RowHint>
          </RowText>
          <ColumnLabel>In app</ColumnLabel>
          <ColumnLabel>Push</ColumnLabel>
        </HeaderRow>

        {/* Likes were merged into Cherish: nothing sends a "like" any more, so its row is
            gone. The server folded each member's old Likes switch into Cherishes. */}
        {NOTIFICATION_CATEGORIES.filter((category) => category !== 'likes').map((category) => {
          const channels = channelsFor(category);
          const meta = CATEGORY_META[category];
          return (
            <Row key={category} data-testid={`category-${category}`}>
              <RowText>
                <RowLabel>{meta.label}</RowLabel>
                <RowHint>{meta.hint}</RowHint>
              </RowText>
              <Cell>
                <Switch
                  checked={channels.inApp}
                  onChange={(next) => void setChannel(category, 'inApp', next)}
                  label={`${meta.label} in app`}
                />
              </Cell>
              <Cell>
                <Switch
                  checked={channels.push}
                  onChange={(next) => void setChannel(category, 'push', next)}
                  label={`${meta.label} push`}
                />
              </Cell>
            </Row>
          );
        })}

        <Row data-testid="category-council">
          <RowText>
            <RowLabel>Moderation updates</RowLabel>
            <RowHint>
              When your post goes under review, when a Council decides, and when an appeal is
              answered. These are always sent, so a decision about your own content can never
              reach you silently.
            </RowHint>
          </RowText>
          <LockedNote>Always on</LockedNote>
        </Row>
      </Card>

      <Card>
        <CardTitle>Creators you follow</CardTitle>
        <RowHint>
          Post notifications are on for everyone you follow. Switch off any creator you would
          rather not hear from. You stay following them either way, and their posts still appear
          in your feed.
        </RowHint>

        <Disclosure
          type="button"
          aria-expanded={creatorsOpen}
          onClick={() => {
            // Clearing the error here is what keeps stale "could not load" text
            // from rendering NEXT TO a successfully retried list — the effect
            // refires on reopen (creatorsError is not in its guard) but nothing
            // else ever reset the flag.
            setCreatorsError(false);
            setCreatorsOpen((o) => !o);
          }}
        >
          <Chevron $open={creatorsOpen}>
            <Icon name="chevron-down" size={16} />
          </Chevron>
          Choose creators
          {creators !== null && ` (${creators.length})`}
        </Disclosure>

        {creatorsOpen && (
          <div data-testid="creator-list">
            {postsOff && (
              <Muted>
                Post notifications are switched off above, so none of these will be sent.
              </Muted>
            )}
            {creatorsError && <Muted>Could not load the people you follow.</Muted>}
            {!creatorsError && creators === null && <Muted>Loading…</Muted>}
            {!creatorsError && creators !== null && creators.length === 0 && (
              <Muted>You are not following anyone yet.</Muted>
            )}
            {creators?.map((creator) => (
              <CreatorRow key={creator.handle}>
                <Avatar name={creator.name} avatarKey={creator.avatarKey} size={28} />
                <RowText>
                  <CreatorName>{creator.name}</CreatorName>
                  <CreatorHandle>{creator.handle}</CreatorHandle>
                </RowText>
                <Cell>
                  <Switch
                    checked={creator.notifyPosts}
                    onChange={(next) => void setCreator(creator.handle, next)}
                    label={`Post notifications from ${creator.name}`}
                    disabled={postsOff}
                  />
                </Cell>
              </CreatorRow>
            ))}
          </div>
        )}
      </Card>
    </Stack>
  );
}
